import { Injectable } from '@angular/core';
import {
    CreateOrUpdateUserStateRequest,
    ListUserStatesRequest,
    UserState,
    UserStateStatus
} from '@lsq-frontend/remote-services/enterprise/common';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { ReplaySubject } from 'rxjs';
import { first } from 'rxjs/operators';
import { UserStates } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    CreateOrUpdateUserStateService,
    ListUserStatesService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';

@Injectable({
    providedIn: 'root'
})
export class UserStateService {
    private _userStatesSubject = new ReplaySubject<UserState[]>(1);
    public userStates$ = this._userStatesSubject.asObservable();

    constructor(
        private _userService: UserService,
        private _listUserStatesService: ListUserStatesService,
        private _createOrUpdateUserStateService: CreateOrUpdateUserStateService
    ) {
        SingletonServiceGuard.register('UserStateService');
        this._userService.user$.subscribe(async (user) => {
            if (user) {
                await this.fetchStates();
            }
        });

        // Activate the stream.
        this.userStates$.subscribe();
    }

    public async fetchStates(): Promise<void> {
        const response = await this._listUserStatesService.executeOnce(new ListUserStatesRequest());
        this._userStatesSubject.next(response.rows);
    }

    public async createOrUpdateUserState(
        userStateType: UserStates | string,
        userStateStatus?: UserStateStatus,
        jsonData?: object
    ): Promise<void> {
        await this._createOrUpdateUserStateService.executeOnce(
            new CreateOrUpdateUserStateRequest({
                userStateType,
                userStateStatus,
                jsonData: jsonData ? JSON.stringify(jsonData) : null
            })
        );
        await this.fetchStates();
    }

    public async getUserState(userStateType: UserStates | string): Promise<UserState> {
        return (await this.userStates$.pipe(first()).toPromise()).find(
            (userState) => userState.userStateType === userStateType
        );
    }
}
